import { LayerPattern } from '@/generated/model/layerPattern'
import { PalletPattern } from '@/generated/model/palletPattern'
import { MachineSettings } from '@/generated/model/machineSettings'
import { BoxPosition } from '@/generated/model/boxPosition'
import { RobotTypeEnum } from '@/generated/model/robotTypeEnum'
import { isRI20, detectGripperPayloadOverlapAlt, isGripperInsidePalletSingleBox2, isRotationAllowed2 } from '@/libs/pattern/gripperUtils'

const checkPositions = (pattern: PalletPattern, machineSettings: MachineSettings, pos: BoxPosition, groups: number[][], gripperSections: number[][], pusherGroups: number[], ignoreList: number[], side: string) => {
  return !ignoreList.includes(pos.id!)
    && !isGripperInsidePalletSingleBox2(pos, pattern, machineSettings, side, groups, gripperSections, pusherGroups)
}

const checkRotationPositions = (pattern: PalletPattern, machineSettings: MachineSettings, pos: BoxPosition, groups: number[][], ignoreList: number[], side: string) => {
  return !(groups && groups.flat().includes(pos.id!))
    && !ignoreList.includes(pos.id!)
    && !isRotationAllowed2(pos, pattern, side, machineSettings)
}

export const layerPatternContainsGripperIssues = (lp: LayerPattern, pattern: PalletPattern, machineSettings: MachineSettings, side: string): boolean => {
  // check if box has gripper issue (= not on ignore list and not inside of pallet)
  if (!isRI20(machineSettings.robotType!)) {
    return false
  }
  return  side === 'L' ?
      lp.boxPositions!.some(pos => checkPositions (pattern, machineSettings, pos, lp.boxGroups!, lp.gripperSections!, lp.pusher!, lp.ignoreGripper!, side)) :
      lp.boxPositionsAlt!.some(pos => checkPositions (pattern, machineSettings, pos, lp.boxGroupsAlt!, lp.gripperSectionsAlt!, lp.pusherAlt!, lp.ignoreGripperAlt!, side))
}

export const layerPatternContainsCollisions = (lp: LayerPattern, pattern: PalletPattern, machineSettings: MachineSettings, side: string): boolean => {
  const overlapMap = detectGripperPayloadOverlapAlt(lp, pattern, machineSettings, side)
  return overlapMap.size > 0
}

export const layerPatternContainsRotationIssues = (lp: LayerPattern, pattern: PalletPattern, machineSettings: MachineSettings, side: string): boolean => {
  return side === 'L' ?
    lp.boxPositions!.some(pos => checkRotationPositions (pattern, machineSettings, pos, lp.boxGroups!, lp.ignoreRotation!, side)) :
    lp.boxPositionsAlt!.some(pos => checkRotationPositions (pattern, machineSettings, pos, lp.boxGroupsAlt!, lp.ignoreRotationAlt!, side))
}

export const checkPatternCompatibility = (layerPatterns: LayerPattern[], machineSettings: MachineSettings, patternRobotType: RobotTypeEnum, checkDoublePlace: boolean = false): string[]  => {
  // Custom Solution uses its own sequence/pick workflow instead of the standard machine compatibility checks.
  if (patternRobotType === RobotTypeEnum.CUSTOM_SOLUTION) {
    return []
  }
  const compatibilityIssues: string[] = []
  // check pattern compatibility
  for (const lp of layerPatterns) {
    // check pusher (currently only available for RI20, so also check if pattern has right robot type)
    if ((!machineSettings.pusher || !isRI20(patternRobotType))
      && !compatibilityIssues.includes ('pusher')) {
      const allPusherGroups = [lp.pusher, lp.pusherAlt]
      for (const pg of allPusherGroups) {
        if (pg && pg.length > 0) {
          // console.log('checkCompatibility - pattern was created with pusher enabled')
          compatibilityIssues.push ('pusher')
          break
        }
      }
    }
  }
  // check gripper sections (currently 4 and 3 sections are only available for RI20, so also check if pattern has right robot type)
  if (!compatibilityIssues.includes ('sections')) {
    if (hasDifferentSections(layerPatterns, machineSettings, patternRobotType)) {
      compatibilityIssues.push('sections')
    }
  }
  if (checkDoublePlace && !compatibilityIssues.includes('doubleplace')) {
    if (hasDoublePlaceOptionIssue(layerPatterns, machineSettings)) {
      compatibilityIssues.push('doubleplace')
    }
  }
  return compatibilityIssues
}

export const hasDifferentSections = (layerPatterns: LayerPattern[], machineSettings: MachineSettings, patternRobotType: RobotTypeEnum): boolean => {
  for (const lp of layerPatterns) {
    const allSections = [...lp.gripperSections!, ...lp.gripperSectionsAlt!]
    for (const sections of allSections) {
      if (!gripperSectionsAreOk (patternRobotType, sections, machineSettings.gripperSettings!.gripperSections!)) {
        return true
      }
    }
  }
  return false
}

const gripperSectionsAreOk = (patternRobotType: RobotTypeEnum, patternSections: number[], machineGripperSections: number): boolean => {
  return !patternSections
    || (patternSections.length < 3 && !isRI20 (patternRobotType))
    || (machineGripperSections === patternSections.length
      && (patternSections.length >= 2 && isRI20 (patternRobotType)))
}

export const updateLayerIssues = (layerIssues: Map<string, Array<{side: string, hasIssue: boolean, ignoredIssues: boolean}>>,
                                  issuesMap: Map<string, number[]>,
                                  ignoredMap: Map<string, number[]>,
                                  layerPatterns: LayerPattern[],
                                  layerId: number): Map<string, Array<{side: string, hasIssue: boolean, ignoredIssues: boolean}>> => {
  const index = (layerId >= 3) ? layerId - 3 : layerId
  const activeLPName = layerPatterns[index].name
  const hasIssues: Array<{side: string, hasIssue: boolean, ignoredIssues: boolean}> = []
  hasIssues.push({side: 'L', hasIssue: false, ignoredIssues: false })
  hasIssues.push({side: 'R', hasIssue: false, ignoredIssues: false})
  issuesMap.forEach ((v, key) => {
    if (v.some(id => id < 3)) {
      hasIssues.find(i => i.side === 'L')!.hasIssue = true
    }
    if (v.some(id => id >= 3)) {
      hasIssues.find(i => i.side === 'R')!.hasIssue = true
    }
  })
  ignoredMap.forEach((v, key) => {
    if (v.some(id => id < 3)) {
      hasIssues.find(i => i.side === 'L')!.ignoredIssues = true
    }
    if (v.some(id => id >= 3)) {
      hasIssues.find(i => i.side === 'R')!.ignoredIssues = true
    }
  })
  // console.log('updateAllLayerIssues - layerIssues before update', JSON.stringify([...layerIssues.entries()]), JSON.stringify(hasIssues))
  if (layerIssues.get(activeLPName!) == null
    || !layerIssues.has(activeLPName!)
    || JSON.stringify(layerIssues.get(activeLPName!)) !== JSON.stringify(hasIssues)) {
    layerIssues.set(activeLPName!, hasIssues)
  }
  // console.log('updateAllLayerIssues - layerIssues after update', JSON.stringify([...layerIssues.entries()]))
  return layerIssues
}

export const setLayerIssuesValues = (layerPatterns: LayerPattern[],
                                     palletPattern: PalletPattern,
                                     machineSettings: MachineSettings): Map<string, Array<{side: string, hasIssue: boolean, ignoredIssues: boolean}>> => {
  let layerIssues = new Map()
  for (const layerPattern of layerPatterns) {
    const leftGripperIssues = layerPatternContainsGripperIssues(layerPattern, palletPattern, machineSettings, 'L')
    const leftRotationIssues = layerPatternContainsRotationIssues(layerPattern, palletPattern, machineSettings, 'L')
    const leftCollisions = layerPatternContainsCollisions(layerPattern, palletPattern, machineSettings, 'L')
    const hasLeftSideIssues = leftGripperIssues || leftRotationIssues || leftCollisions
    const hasLeftSideIgnoredIssues = layerPattern.ignoreGripper!.length > 0 || layerPattern.ignoreRotation!.length > 0

    const rightGripperIssues = layerPatternContainsGripperIssues(layerPattern, palletPattern, machineSettings, 'R')
    const rightRotationIssues = layerPatternContainsRotationIssues(layerPattern, palletPattern, machineSettings, 'R')
    const rightCollisions = layerPatternContainsCollisions(layerPattern, palletPattern, machineSettings, 'R')
    const hasRightSideIssues = rightGripperIssues || rightRotationIssues || rightCollisions
    const hasRightSideIgnoredIssues = layerPattern.ignoreGripperAlt!.length > 0 || layerPattern.ignoreRotationAlt!.length > 0
    const result = [
      {side: 'L', hasIssue: hasLeftSideIssues, ignoredIssues: hasLeftSideIgnoredIssues},
      {side: 'R', hasIssue: hasRightSideIssues, ignoredIssues: hasRightSideIgnoredIssues}
    ]
    layerIssues = new Map(layerIssues.set(layerPattern.name!.toString(), result))
  }
  // console.log('setLayerIssuesValues - result: ', JSON.stringify([...layerIssues.entries()]))
  return layerIssues
}

export const hasDoublePlaceOptionIssue = (layerPatterns: LayerPattern[], machineSettings: MachineSettings): boolean => {
  if (!isRI20(machineSettings.robotType!) || machineSettings.gripperSettings!.gripperSections !== 2 || !machineSettings.disableDoublePlace) {
    return false
  }
  // check for double place groups when double place is disabled
  for (const lp of layerPatterns) {
    const allSections = [...lp.gripperSections!, ...lp.gripperSectionsAlt!]
    for (const sections of allSections) {
      if (sections.some(s => s !== 0)) {
        console.log('checkPatternCompatibility - doubleplace check - section: ', JSON.stringify(sections))
        return true
      }
    }
  }
  return false
}

export const populateUniqueBoxIdsLayerPattern = (lp: LayerPattern) => {
  populateUniqueBoxIds(lp.boxPositions!, 0)
  const maxLeftBoxUniqueId = getMaxBoxUID(lp.boxPositions!)
  populateUniqueBoxIds(lp.boxPositionsAlt!, maxLeftBoxUniqueId)
}

export const populateUniqueBoxIds = (boxPositions: BoxPosition[], maxLeftSideBoxUId: number) => {
  boxPositions.forEach(p => {
    if (!p.lpbuId) {
      const maxBoxUid = getMaxBoxUID(boxPositions)
      p.lpbuId = (Math.max(maxLeftSideBoxUId, maxBoxUid)) + 1
    }
  })
}

export const getMaxBoxUID = (positions: BoxPosition[]): number => {
  if (!positions.some(p => p.lpbuId)) {
    return 0
  }
  const positionsUniqueIds = positions.map(p => {
    if (p.lpbuId) {
      return p.lpbuId
    }
    return 0
  })
  return Math.max(...positionsUniqueIds)
}

export const getLayerPatternNameFromValidationDetails = (pattern: PalletPattern): string => {
  if (!pattern.validationStatus || pattern.validationStatus !== 3) {
    return ''
  }
  const layerIndex = pattern.validationDetails!.layerNo! - 1
  return pattern.pallet!.layers![layerIndex].layerPattern!
}
